import { Injectable } from '@angular/core';
import { ScriptTemplate, EventConfig, CanvasContext } from '../interfaces/event-script.models';
import { BuilderComponentType } from '../interfaces/builder-screen.models';

/**
 * Biblioteca de templates prontos para event scripting
 * Analisa o canvas e sugere templates aplicáveis baseado nos componentes disponíveis
 */
@Injectable({ providedIn: 'root' })
export class ScriptTemplateLibraryService {

  /**
   * Templates disponíveis no sistema
   */
  private readonly templates: ScriptTemplate[] = [
    // 1. Switch Controla Tabs
    {
      id: 'switch-controls-tabs',
      name: 'Switch Controla Visibilidade de Abas',
      description: 'Mostrar/esconder ou habilitar/desabilitar abas baseado no estado do switch',
      category: 'control',
      icon: 'swap',
      applicableToTypes: ['switch'],
      requiredComponents: ['tabs'],
      variables: [
        {
          key: 'targetTabIds',
          label: 'Abas a controlar',
          type: 'tab',
          multiple: true
        },
        {
          key: 'whenActive',
          label: 'Quando ativado',
          type: 'value',
          options: [
            { value: 'disable', label: 'Desabilitar abas' },
            { value: 'hide', label: 'Esconder abas' },
            { value: 'enable', label: 'Habilitar abas' },
            { value: 'show', label: 'Mostrar abas' }
          ]
        },
        {
          key: 'whenInactive',
          label: 'Quando desativado',
          type: 'value',
          options: [
            { value: 'enable', label: 'Habilitar abas' },
            { value: 'show', label: 'Mostrar abas' },
            { value: 'disable', label: 'Desabilitar abas' },
            { value: 'hide', label: 'Esconder abas' }
          ]
        }
      ],
      template: {
        id: '', // Será gerado
        eventType: 'onChange',
        mode: 'simple',
        enabled: true,
        templateId: 'switch-controls-tabs',
        simpleConditions: [],
        simpleActions: []
      }
    },

    // 2. Select Muda Panel Ativo
    {
      id: 'select-changes-panel',
      name: 'Select Controla Painéis',
      description: 'Mostrar diferentes painéis baseado na opção selecionada',
      category: 'control',
      icon: 'control',
      applicableToTypes: ['select'],
      requiredComponents: ['panels'],
      variables: [
        {
          key: 'panelMappings',
          label: 'Mapeamento de painéis',
          type: 'panel',
          multiple: true
        }
      ],
      template: {
        id: '',
        eventType: 'onChange',
        mode: 'simple',
        enabled: true,
        templateId: 'select-changes-panel',
        simpleConditions: [],
        simpleActions: []
      }
    },

    // 3. Campo Condicional (mostrar/ocultar campo)
    {
      id: 'conditional-field-visibility',
      name: 'Campo Condicional',
      description: 'Mostrar ou esconder outros campos baseado no valor deste campo',
      category: 'visibility',
      icon: 'eye',
      applicableToTypes: ['switch', 'select', 'text', 'number'],
      requiredComponents: [],
      variables: [
        {
          key: 'condition',
          label: 'Condição',
          type: 'value',
          options: [
            { value: '==', label: 'Igual a' },
            { value: '!=', label: 'Diferente de' },
            { value: '>', label: 'Maior que' },
            { value: '<', label: 'Menor que' },
            { value: 'empty', label: 'Vazio' },
            { value: 'notEmpty', label: 'Não vazio' }
          ]
        },
        {
          key: 'conditionValue',
          label: 'Valor de comparação',
          type: 'value',
          options: []
        },
        {
          key: 'targetFields',
          label: 'Campos a controlar',
          type: 'component',
          multiple: true
        },
        {
          key: 'action',
          label: 'Ação quando condição atendida',
          type: 'value',
          options: [
            { value: 'show', label: 'Mostrar campos' },
            { value: 'hide', label: 'Esconder campos' },
            { value: 'enable', label: 'Habilitar campos' },
            { value: 'disable', label: 'Desabilitar campos' }
          ]
        }
      ],
      template: {
        id: '',
        eventType: 'onChange',
        mode: 'simple',
        enabled: true,
        templateId: 'conditional-field-visibility',
        simpleConditions: [],
        simpleActions: []
      }
    },

    // 4. Validação Dinâmica
    {
      id: 'dynamic-validation',
      name: 'Validação Dinâmica',
      description: 'Desabilitar campos ou botões quando valor é inválido',
      category: 'validation',
      icon: 'check-circle',
      applicableToTypes: ['text', 'number', 'textarea'],
      requiredComponents: [],
      variables: [
        {
          key: 'validationType',
          label: 'Tipo de validação',
          type: 'value',
          options: [
            { value: 'min', label: 'Valor mínimo' },
            { value: 'max', label: 'Valor máximo' },
            { value: 'length', label: 'Tamanho mínimo' },
            { value: 'empty', label: 'Não pode estar vazio' }
          ]
        },
        {
          key: 'validationValue',
          label: 'Valor de validação',
          type: 'value',
          options: []
        },
        {
          key: 'targetComponents',
          label: 'Componentes a desabilitar',
          type: 'component',
          multiple: true,
          componentTypes: ['button', 'text', 'number', 'select']
        }
      ],
      template: {
        id: '',
        eventType: 'onChange',
        mode: 'simple',
        enabled: true,
        templateId: 'dynamic-validation',
        simpleConditions: [],
        simpleActions: []
      }
    },

    // 5. Tab Condicional
    {
      id: 'conditional-tab',
      name: 'Tab Condicional',
      description: 'Mostrar ou habilitar aba apenas quando condição for atendida',
      category: 'control',
      icon: 'menu',
      applicableToTypes: ['switch', 'select', 'number'],
      requiredComponents: ['tabs'],
      variables: [
        {
          key: 'condition',
          label: 'Condição',
          type: 'value',
          options: [
            { value: '==', label: 'Igual a' },
            { value: '!=', label: 'Diferente de' },
            { value: '>', label: 'Maior que' },
            { value: '<', label: 'Menor que' }
          ]
        },
        {
          key: 'conditionValue',
          label: 'Valor de comparação',
          type: 'value',
          options: []
        },
        {
          key: 'targetTabs',
          label: 'Abas a controlar',
          type: 'tab',
          multiple: true
        },
        {
          key: 'action',
          label: 'Ação quando condição atendida',
          type: 'value',
          options: [
            { value: 'show', label: 'Mostrar aba' },
            { value: 'enable', label: 'Habilitar aba' }
          ]
        }
      ],
      template: {
        id: '',
        eventType: 'onChange',
        mode: 'simple',
        enabled: true,
        templateId: 'conditional-tab',
        simpleConditions: [],
        simpleActions: []
      }
    },

    // 6. Campos em Cascata
    {
      id: 'cascade-fields',
      name: 'Campos em Cascata',
      description: 'Preencher automaticamente outros campos baseado no valor selecionado',
      category: 'control',
      icon: 'link',
      applicableToTypes: ['select', 'text'],
      requiredComponents: [],
      variables: [
        {
          key: 'targetFields',
          label: 'Campos a preencher',
          type: 'component',
          multiple: true,
          componentTypes: ['text', 'textarea', 'number']
        }
      ],
      template: {
        id: '',
        eventType: 'onChange',
        mode: 'simple',
        enabled: true,
        templateId: 'cascade-fields',
        description: 'Campos preenchidos automaticamente',
        simpleConditions: [],
        simpleActions: []
      }
    }
  ];

  /**
   * Obtém todos os templates disponíveis
   */
  getAllTemplates(): ScriptTemplate[] {
    return [...this.templates];
  }

  /**
   * Obtém template por ID
   */
  getTemplateById(id: string): ScriptTemplate | undefined {
    return this.templates.find(t => t.id === id);
  }

  /**
   * Retorna templates aplicáveis ao tipo de componente e contexto do canvas
   * @param componentType Tipo do componente selecionado
   * @param canvasContext Contexto do canvas (tabs, panels, fields disponíveis)
   */
  getApplicableTemplates(
    componentType: BuilderComponentType,
    canvasContext: CanvasContext
  ): ScriptTemplate[] {
    return this.templates.filter(template => {
      // Verifica se o template é aplicável ao tipo do componente
      if (!template.applicableToTypes.includes(componentType)) {
        return false;
      }

      // Verifica se os componentes necessários existem no canvas
      return this.hasRequiredComponents(template, canvasContext);
    });
  }

  /**
   * Verifica se o canvas tem os componentes necessários para o template
   */
  private hasRequiredComponents(template: ScriptTemplate, context: CanvasContext): boolean {
    for (const required of template.requiredComponents) {
      switch (required) {
        case 'tabs':
          if (context.availableTabs.length === 0) {
            return false;
          }
          break;
        case 'panels':
          if (context.availablePanels.length === 0) {
            return false;
          }
          break;
        case 'fields':
          if (context.availableFields.length === 0) {
            return false;
          }
          break;
      }
    }
    return true;
  }

  /**
   * Instancia um template com valores específicos
   * @param templateId ID do template
   * @param variableValues Valores das variáveis do template
   * @returns EventConfig preenchido
   */
  instantiateTemplate(
    templateId: string,
    variableValues: Record<string, any>
  ): EventConfig | null {
    const template = this.getTemplateById(templateId);
    if (!template) {
      return null;
    }

    const eventConfig: EventConfig = {
      ...template.template,
      id: this.generateEventId(),
      description: this.buildDescription(template, variableValues)
    };

    // Construir condições e ações baseado no template específico
    switch (templateId) {
      case 'switch-controls-tabs':
        eventConfig.simpleConditions = [
          { field: 'self', operator: '==', value: true }
        ];
        eventConfig.simpleActions = this.buildSwitchTabActions(variableValues);
        break;

      case 'conditional-field-visibility':
        eventConfig.simpleConditions = [
          {
            field: 'self',
            operator: variableValues.condition,
            value: variableValues.conditionValue
          }
        ];
        eventConfig.simpleActions = this.buildFieldVisibilityActions(variableValues);
        break;

      case 'conditional-tab':
        eventConfig.simpleConditions = [
          {
            field: 'self',
            operator: variableValues.condition,
            value: variableValues.conditionValue
          }
        ];
        eventConfig.simpleActions = this.buildTabConditionalActions(variableValues);
        break;

      case 'dynamic-validation':
        eventConfig.simpleConditions = this.buildValidationConditions(variableValues);
        eventConfig.simpleActions = this.buildValidationActions(variableValues);
        break;

      // Adicionar outros templates conforme necessário
    }

    return eventConfig;
  }

  /**
   * Gera descrição humanizada do evento
   */
  private buildDescription(template: ScriptTemplate, values: Record<string, any>): string {
    // Implementar descrição específica por template
    switch (template.id) {
      case 'switch-controls-tabs':
        const action = values.whenActive === 'disable' ? 'desabilitar' : 'habilitar';
        const tabs = Array.isArray(values.targetTabIds) ? values.targetTabIds.join(', ') : '';
        return `Quando ativado, ${action} as abas: ${tabs}`;
      
      default:
        return template.description;
    }
  }

  /**
   * Constrói ações para template switch-controls-tabs
   */
  private buildSwitchTabActions(values: Record<string, any>): any[] {
    const actions: any[] = [];
    const targetTabs = Array.isArray(values.targetTabIds) ? values.targetTabIds : [values.targetTabIds];
    
    targetTabs.forEach(tabId => {
      if (values.whenActive === 'disable') {
        actions.push({ type: 'disableTab', targetId: tabId });
      } else if (values.whenActive === 'hide') {
        actions.push({ type: 'hideTab', targetId: tabId });
      } else if (values.whenActive === 'enable') {
        actions.push({ type: 'enableTab', targetId: tabId });
      } else if (values.whenActive === 'show') {
        actions.push({ type: 'showTab', targetId: tabId });
      }
    });

    return actions;
  }

  /**
   * Constrói ações para template conditional-field-visibility
   */
  private buildFieldVisibilityActions(values: Record<string, any>): any[] {
    const actions: any[] = [];
    const targetFields = Array.isArray(values.targetFields) ? values.targetFields : [values.targetFields];
    
    targetFields.forEach(fieldId => {
      if (values.action === 'show') {
        actions.push({ type: 'showComponent', targetId: fieldId });
      } else if (values.action === 'hide') {
        actions.push({ type: 'hideComponent', targetId: fieldId });
      } else if (values.action === 'enable') {
        actions.push({ type: 'enableComponent', targetId: fieldId });
      } else if (values.action === 'disable') {
        actions.push({ type: 'disableComponent', targetId: fieldId });
      }
    });

    return actions;
  }

  /**
   * Constrói ações para template conditional-tab
   */
  private buildTabConditionalActions(values: Record<string, any>): any[] {
    const actions: any[] = [];
    const targetTabs = Array.isArray(values.targetTabs) ? values.targetTabs : [values.targetTabs];
    
    targetTabs.forEach(tabId => {
      if (values.action === 'show') {
        actions.push({ type: 'showTab', targetId: tabId });
      } else if (values.action === 'enable') {
        actions.push({ type: 'enableTab', targetId: tabId });
      }
    });

    return actions;
  }

  /**
   * Constrói condições para validação dinâmica
   */
  private buildValidationConditions(values: Record<string, any>): any[] {
    const conditions: any[] = [];
    
    switch (values.validationType) {
      case 'min':
        conditions.push({ field: 'self', operator: '>=', value: values.validationValue });
        break;
      case 'max':
        conditions.push({ field: 'self', operator: '<=', value: values.validationValue });
        break;
      case 'empty':
        conditions.push({ field: 'self', operator: 'notEmpty', value: undefined });
        break;
    }

    return conditions;
  }

  /**
   * Constrói ações para validação dinâmica
   */
  private buildValidationActions(values: Record<string, any>): any[] {
    const actions: any[] = [];
    const targets = Array.isArray(values.targetComponents) ? values.targetComponents : [values.targetComponents];
    
    targets.forEach(targetId => {
      actions.push({ type: 'enableComponent', targetId });
    });

    return actions;
  }

  /**
   * Gera ID único para evento
   */
  private generateEventId(): string {
    return `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}



